import {
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
  withStyles,
} from "@material-ui/core"
import Tooltip from "@material-ui/core/Tooltip"
import dayjs from "dayjs"
import { useState, Dispatch, SetStateAction } from "react"
import CircleCheckSolid from "../../assets/CircleCheckSolid.svg"
import CircleRegular from "../../assets/CircleRegular.svg"
import {
  NudgeScheduleFragment,
  QuestionnaireScheduleFragment,
  useGetNudgeTakesByScheduleIdQuery,
  useGetQuestionnaireTakesByScheduleIdQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { isNudgeSchedule, isQuestionnaireSchedule } from "../../utils/helpers"
import {
  getItemDataFromScheduleItem,
  isInactiveInFuture,
  isSystemAccountPractitioner,
  noChangeAllowed,
  takeCompletionOnDate,
} from "../../utils/scheduleUtils"
import { colors } from "../../utils/theme"
import { ScheduleItem } from "../../utils/types"
import MoreIcon from "../atoms/MoreIcon"
import DisabledActionTooltip from "./DisabledActionTooltip"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

interface ScheduledItemCardProps {
  scheduleDate: string
  scheduleItem: NudgeScheduleFragment | QuestionnaireScheduleFragment
  canSchedule: boolean
  setCurrentItem: Dispatch<SetStateAction<ScheduleItem | undefined>>
  setOpenScheduleDialog: Dispatch<SetStateAction<boolean>>
  setOpenDeleteDialog: Dispatch<SetStateAction<boolean>>
  setCurrentDate: (value: SetStateAction<dayjs.Dayjs | undefined>) => void
}

export default function ScheduledItemCard({
  scheduleDate,
  scheduleItem,
  canSchedule,
  setCurrentItem,
  setOpenScheduleDialog,
  setOpenDeleteDialog,
  setCurrentDate,
}: ScheduledItemCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const graphqlClient = useGraphQLClient()
  const classes = useStyles()
  const { scheduledAt, name, message, takes } =
    getItemDataFromScheduleItem(scheduleItem)
  const inactive = isInactiveInFuture(scheduleItem)
  const completedAt = takeCompletionOnDate(
    scheduleDate,
    takes,
    isNudgeSchedule(scheduleItem)
  )
  const staticNoChangesAllowed = noChangeAllowed(scheduleItem)
  const isSystemAccount = isSystemAccountPractitioner(scheduleItem)

  // Only fire live-count queries for one-time items that aren't already locked,
  // and only once the menu has been opened
  const shouldQueryLive =
    menuAnchor !== null && !!scheduleItem.one_time && !staticNoChangesAllowed
  const isNudge = isNudgeSchedule(scheduleItem)

  const { data: nudgeTakesData, isLoading: nudgeTakesLoading } =
    useGetNudgeTakesByScheduleIdQuery(
      graphqlClient,
      { id: scheduleItem.id },
      { enabled: shouldQueryLive && isNudge }
    )
  const { data: questionnaireTakesData, isLoading: questionnaireTakesLoading } =
    useGetQuestionnaireTakesByScheduleIdQuery(
      graphqlClient,
      { id: scheduleItem.id },
      { enabled: shouldQueryLive && !isNudge }
    )

  const liveQueryInFlight = shouldQueryLive
    ? isNudge
      ? nudgeTakesLoading
      : questionnaireTakesLoading
    : false

  const liveTakeExists = isNudge
    ? (nudgeTakesData?.nudge_takes?.length ?? 0) > 0
    : (questionnaireTakesData?.questionnaire_takes?.length ?? 0) > 0

  const noChangesAllowed =
    staticNoChangesAllowed || liveQueryInFlight || liveTakeExists
  const editDisabled = !canSchedule || noChangesAllowed

  return (
    <div
      className={`${
        isSystemAccount ? classes.cardSystemAccount : classes.card
      } ${inactive && classes.inactive}`}
    >
      <div className={classes.leftContainer}>
        {dayjs(scheduleDate).isBefore(dayjs()) && (
          <HtmlTooltip
            placement="bottom-start"
            title={
              <Typography>
                {completedAt
                  ? `Completed on ${dayjs(completedAt).format(
                      "M/D/YYYY [at] h:mm a"
                    )}`
                  : "Not Completed"}
              </Typography>
            }
          >
            <img
              src={completedAt ? CircleCheckSolid : CircleRegular}
              height={25}
              width={25}
              alt={completedAt ? "completed" : "not-completed"}
            />
          </HtmlTooltip>
        )}
        <span>{dayjs(scheduledAt).format("h:mm a")}</span>
        <div className={classes.content}>
          <strong>{name}</strong>
          <span>{message}</span>
        </div>
      </div>
      <IconButton
        className={classes.iconButton}
        onClick={(event) => {
          setMenuAnchor(event.currentTarget)
          setCurrentItem(scheduleItem)
          setCurrentDate(dayjs(scheduleDate))
        }}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        {((isNudgeSchedule(scheduleItem) && scheduleItem.nudge) ||
          (isQuestionnaireSchedule(scheduleItem) &&
            scheduleItem.questionnaire)) && ( // handles edge case where practitioner doesn't have permission to edit nudge
          <DisabledActionTooltip
            disabled={!canSchedule}
            text={SCHEDULING_DISABLED_TOOLTIP}
          >
            <span>
              <MenuItem
                onClick={() => {
                  setOpenScheduleDialog(true)
                  setMenuAnchor(null)
                }}
                disabled={editDisabled}
              >
                Edit
              </MenuItem>
            </span>
          </DisabledActionTooltip>
        )}
        <MenuItem
          onClick={() => {
            setOpenDeleteDialog(true)
            setMenuAnchor(null)
          }}
          disabled={editDisabled}
        >
          Remove
        </MenuItem>
      </Menu>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    boxShadow: "none",
    width: "100%",
    padding: theme.spacing(1, 2, 1, 1.5),
    borderRadius: 4,
    textAlign: "left",
    backgroundColor: "white",
    border: `1px solid ${colors.neutral.cement}`,
    borderLeft: `4px solid ${colors.tertiary.violet}`,
  },
  inactive: {
    backgroundColor: "rgba(255,255,255,0.2)",
    borderLeft: `4px solid ${colors.neutral.cement}`,
    textDecorationLine: "line-through",
  },
  leftContainer: {
    display: "flex",
    "& > *": {
      marginRight: "12px",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    "& > strong": {
      color: "black",
    },
  },
  iconButton: {
    border: 0,
    margin: 0,
    padding: 0,
    height: 24,
    width: 24,
  },
  cardSystemAccount: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    boxShadow: "none",
    width: "100%",
    padding: theme.spacing(1, 2, 1, 1.5),
    borderRadius: 4,
    textAlign: "left",
    backgroundColor: "white",
    border: `1px solid ${colors.neutral.cement}`,
    borderLeft: `4px solid ${colors.tertiary.orange}`,
  },
}))

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}))(Tooltip)
